import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Sparkles,
  Brain,
  Shield,
  Send,
  Loader2,
  CheckCircle,
  XCircle,
  Database,
  Zap,
  Clock,
  BarChart3,
  MessageSquare,
  Eye,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Cpu,
  Activity,
  Server,
  BookOpen,
  Scale,
  Flame,
  FileText,
  Trash2
} from 'lucide-react';
import api from '../services/api';
import { useToast } from '../hooks/use-toast';

const AlfaFederated = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [category, setCategory] = useState('general');
  const [includeMemory, setIncludeMemory] = useState(true);
  const [includeLaw, setIncludeLaw] = useState(true);
  
  // Response
  const [response, setResponse] = useState(null);
  const [showReasoning, setShowReasoning] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(false);
  
  // Stats
  const [stats, setStats] = useState(null);
  const [models, setModels] = useState([]);
  const [memories, setMemories] = useState([]);
  const [history, setHistory] = useState([]);
  
  const messagesEndRef = useRef(null);

  const categories = [
    { value: 'general', label: 'Général', icon: '💬' },
    { value: 'security', label: 'Sécurité', icon: '🔒' },
    { value: 'legal', label: 'Juridique', icon: '⚖️' },
    { value: 'hr', label: 'RH', icon: '👥' },
    { value: 'operations', label: 'Opérations', icon: '⚙️' },
    { value: 'finance', label: 'Finance', icon: '💰' }
  ];

  useEffect(() => {
    fetchStats();
    fetchModels();
    fetchMemories();
    fetchHistory();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await api.get('/alfa/stats');
      setStats(res.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchModels = async () => {
    try {
      const res = await api.get('/alfa/models');
      setModels(res.data.models || []);
    } catch (error) {
      console.error('Error fetching models:', error);
    }
  };

  const fetchMemories = async () => {
    try {
      const res = await api.get('/alfa/memories?limit=10');
      setMemories(res.data || []);
    } catch (error) {
      console.error('Error fetching memories:', error);
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await api.get('/alfa/history?limit=10');
      setHistory(res.data || []);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const sendMessage = async () => {
    if (!message.trim()) {
      toast({ title: 'Erreur', description: 'Entrez votre question', variant: 'destructive' });
      return;
    }

    setLoading(true);
    setResponse(null);

    try {
      const res = await api.post('/alfa/chat', {
        message,
        category,
        include_memory: includeMemory,
        include_law_articles: includeLaw
      });

      setResponse(res.data);
      setMessage('');
      fetchStats();
      fetchMemories();
      fetchHistory();

      if (res.data.guard_passed) {
        toast({ 
          title: '🧬 ALFA a répondu', 
          description: `Traité en ${res.data.processing_time_ms}ms avec ${res.data.models_used?.length || 0} modèles` 
        });
      } else {
        toast({ 
          title: '🛡️ Requête bloquée', 
          description: res.data.guard_message,
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({ 
        title: 'Erreur', 
        description: error.response?.data?.detail || 'Communication impossible', 
        variant: 'destructive' 
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteMemory = async (memoryId) => {
    try {
      await api.delete(`/alfa/memories/${memoryId}`);
      fetchMemories();
      toast({ title: 'Mémoire supprimée' });
    } catch (error) {
      toast({ title: 'Erreur', variant: 'destructive' });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 text-white p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="relative">
              <div className="p-4 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-2xl shadow-lg shadow-purple-500/30">
                <Sparkles className="w-10 h-10 text-white" />
              </div>
              <div className="absolute -top-1 -right-1 bg-green-500 rounded-full p-1.5">
                <Activity className="w-3 h-3 text-white animate-pulse" />
              </div>
            </div>
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                ALFA FÉDÉRÉ
              </h1>
              <p className="text-slate-400">Architecture IA Hybride Multi-Modèle</p>
            </div>
          </div>
          
          {/* System Status */}
          <div className="flex items-center gap-3 bg-slate-800/50 rounded-xl px-4 py-2">
            <Server className="w-5 h-5 text-green-400" />
            <div>
              <p className="text-xs text-slate-400">Statut Système</p>
              <p className="text-lg font-bold text-green-400">En ligne</p>
            </div>
          </div>
        </div>

        {/* Architecture Visual */}
        <Card className="bg-gradient-to-r from-slate-800/50 to-slate-900/50 border-slate-700 mb-6 overflow-hidden">
          <CardContent className="py-4">
            <div className="flex items-center justify-center gap-2 text-xs flex-wrap">
              <span className="px-3 py-1.5 bg-blue-900/50 border border-blue-500/50 rounded-lg text-blue-300 flex items-center gap-1">
                📥 Input
              </span>
              <span className="text-slate-500">→</span>
              <span className="px-3 py-1.5 bg-orange-900/50 border border-orange-500/50 rounded-lg text-orange-300 flex items-center gap-1">
                🛡️ Mistral
              </span>
              <span className="text-slate-500">→</span>
              <span className="px-3 py-1.5 bg-purple-900/50 border border-purple-500/50 rounded-lg text-purple-300 flex items-center gap-1">
                🔗 Fédération Alpha
              </span>
              <span className="text-slate-500">→</span>
              <span className="px-3 py-1.5 bg-red-900/50 border border-red-500/50 rounded-lg text-red-300 flex items-center gap-1">
                🔥 Alpha-2
              </span>
              <span className="text-slate-500">→</span>
              <span className="px-3 py-1.5 bg-green-900/50 border border-green-500/50 rounded-lg text-green-300 flex items-center gap-1">
                📝 Post-traitement
              </span>
              <span className="text-slate-500">→</span>
              <span className="px-3 py-1.5 bg-gradient-to-r from-indigo-600 to-pink-600 rounded-lg text-white font-bold">
                📤 Output
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Models Grid - Secured Names */}
        <div className="grid grid-cols-5 gap-3 mb-6">
          {[
            { name: 'Mistral', role: 'Garde-fou', emoji: '🛡️', color: 'from-orange-500 to-amber-600' },
            { name: 'Alpha-1', role: 'Pré-analyse', emoji: '🧠', color: 'from-blue-500 to-cyan-600' },
            { name: 'Alpha-2', role: 'Synthèse', emoji: '🔥', color: 'from-red-500 to-pink-600' },
            { name: 'Alpha-3', role: 'Extraction', emoji: '📝', color: 'from-green-500 to-emerald-600' },
            { name: 'Alpha-4', role: 'Embeddings', emoji: '🧬', color: 'from-purple-500 to-violet-600' }
          ].map((model, i) => (
            <Card key={i} className="bg-slate-800/50 border-slate-700 hover:border-slate-500 transition-all">
              <CardContent className="p-3">
                <div className="flex items-center gap-2">
                  <div className={`p-2 rounded-lg bg-gradient-to-br ${model.color}`}>
                    <span className="text-lg">{model.emoji}</span>
                  </div>
                  <div>
                    <p className="font-semibold text-white text-sm">{model.name}</p>
                    <p className="text-xs text-slate-400">{model.role}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Panel */}
        <div className="lg:col-span-2 space-y-6">
          {/* Input Card */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-pink-400" />
                Interroger ALFA
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Category */}
              <div>
                <label className="text-sm text-slate-400 mb-2 block">Catégorie</label>
                <div className="flex flex-wrap gap-2">
                  {categories.map((cat) => (
                    <Button
                      key={cat.value}
                      variant={category === cat.value ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setCategory(cat.value)}
                      className={category === cat.value ? 'bg-indigo-600' : 'border-slate-600 bg-slate-700/50'}
                    >
                      {cat.icon} {cat.label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Options */}
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeMemory}
                    onChange={(e) => setIncludeMemory(e.target.checked)}
                    className="rounded border-slate-600"
                  />
                  <Database className="w-4 h-4 text-purple-400" />
                  Mémoire
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeLaw}
                    onChange={(e) => setIncludeLaw(e.target.checked)}
                    className="rounded border-slate-600"
                  />
                  <BookOpen className="w-4 h-4 text-blue-400" />
                  Articles de loi
                </label>
              </div>

              {/* Message Input */}
              <div>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && !loading && sendMessage()}
                  placeholder="Posez votre question à ALFA (architecture fédérée multi-modèle)..."
                  className="w-full h-32 bg-slate-700 border-slate-600 rounded-lg p-3 text-white placeholder:text-slate-500 resize-none"
                />
              </div>

              {/* Submit */}
              <div className="flex justify-end">
                <Button
                  onClick={sendMessage}
                  disabled={loading || !message.trim()}
                  className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 hover:opacity-90"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ALFA traite...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Interroger ALFA
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Response Display */}
          {response && (
            <Card className={`border-2 ${response.guard_passed ? 'bg-slate-800/50 border-green-500/50' : 'bg-red-900/20 border-red-500'}`}>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-white">
                    <Sparkles className="w-5 h-5 text-pink-400" />
                    Réponse ALFA
                    {response.guard_passed && <CheckCircle className="w-4 h-4 text-green-400" />}
                  </span>
                  <span className="text-sm font-normal text-slate-400 flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {response.processing_time_ms}ms
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Guard Status */}
                {!response.guard_passed && (
                  <div className="bg-red-900/30 border border-red-500/50 rounded-lg p-3">
                    <div className="flex items-center gap-2 text-red-400">
                      <Shield className="w-5 h-5" />
                      <span className="font-medium">Requête bloquée par le garde-fou</span>
                    </div>
                    <p className="text-sm text-red-300 mt-1">{response.guard_message}</p>
                  </div>
                )}

                {/* Synthesis */}
                {response.guard_passed && (
                  <>
                    <div className="bg-slate-700/50 rounded-lg p-4">
                      <p className="text-slate-200 whitespace-pre-wrap">{response.synthesis}</p>
                    </div>

                    {/* Reasoning Toggle */}
                    {response.reasoning && (
                      <div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowReasoning(!showReasoning)}
                          className="text-slate-400 hover:text-white"
                        >
                          {showReasoning ? <ChevronUp className="w-4 h-4 mr-1" /> : <ChevronDown className="w-4 h-4 mr-1" />}
                          🧠 Raisonnement DeepSeek
                        </Button>
                        {showReasoning && (
                          <div className="bg-slate-900/50 border border-slate-700 rounded-lg p-3 mt-2 text-sm text-slate-400">
                            <pre className="whitespace-pre-wrap">{response.reasoning}</pre>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Llama Analysis Toggle */}
                    {response.llama_analysis && (
                      <div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowAnalysis(!showAnalysis)}
                          className="text-slate-400 hover:text-white"
                        >
                          {showAnalysis ? <ChevronUp className="w-4 h-4 mr-1" /> : <ChevronDown className="w-4 h-4 mr-1" />}
                          🧠 Pré-analyse Alpha-1
                        </Button>
                        {showAnalysis && (
                          <div className="bg-blue-900/20 border border-blue-700 rounded-lg p-3 mt-2 text-sm text-blue-300">
                            <pre className="whitespace-pre-wrap">{response.llama_analysis}</pre>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Law Articles */}
                    {response.law_articles?.length > 0 && (
                      <div className="bg-amber-900/20 border border-amber-700/50 rounded-lg p-3">
                        <p className="text-sm font-medium text-amber-400 mb-2 flex items-center gap-1">
                          <Scale className="w-4 h-4" /> Articles de loi utilisés
                        </p>
                        {response.law_articles.map((article, i) => (
                          <div key={i} className="text-xs text-amber-300 py-1">
                            • Art. {article.article}: {article.title}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Extracted Memories */}
                    {response.extracted_memories?.length > 0 && (
                      <div className="bg-purple-900/20 border border-purple-700/50 rounded-lg p-3">
                        <p className="text-sm font-medium text-purple-400 mb-2 flex items-center gap-1">
                          <Database className="w-4 h-4" /> Nouvelles mémoires extraites
                        </p>
                        {response.extracted_memories.map((mem, i) => (
                          <div key={i} className="text-xs text-purple-300 py-1">
                            + {mem}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Models Used */}
                    <div className="flex flex-wrap gap-2">
                      {response.models_used?.map((model, i) => (
                        <span key={i} className="px-2 py-1 bg-slate-700 rounded text-xs text-slate-300">
                          {model.split('/').pop()}
                        </span>
                      ))}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Stats */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-indigo-400" />
                Statistiques
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-purple-900/30 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-white">{stats?.total_memories || 0}</p>
                  <p className="text-xs text-slate-400">Mémoires</p>
                </div>
                <div className="bg-blue-900/30 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-white">{stats?.total_conversations || 0}</p>
                  <p className="text-xs text-slate-400">Conversations</p>
                </div>
              </div>
              
              {stats?.memory_types && (
                <div className="space-y-1">
                  <p className="text-xs text-slate-400">Par type</p>
                  <div className="grid grid-cols-2 gap-1">
                    <span className="text-xs text-slate-300">📋 Faits: {stats.memory_types.fact || 0}</span>
                    <span className="text-xs text-slate-300">⭐ Préf: {stats.memory_types.preference || 0}</span>
                    <span className="text-xs text-slate-300">📝 Proc: {stats.memory_types.procedure || 0}</span>
                    <span className="text-xs text-slate-300">💭 Ctx: {stats.memory_types.context || 0}</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Architecture Info */}
          <Card className="bg-gradient-to-br from-indigo-900/50 to-purple-900/50 border-indigo-700/50">
            <CardContent className="pt-6">
              <h3 className="font-bold text-white mb-3 flex items-center gap-2">
                <Zap className="w-5 h-5 text-yellow-400" />
                Architecture Fédérée
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-start gap-2">
                  <span className="text-orange-400">🛡️</span>
                  <div>
                    <strong className="text-white">Mistral 3.2</strong>
                    <p className="text-slate-400 text-xs">Garde-fou entrée/sortie</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-blue-400">🧠</span>
                  <div>
                    <strong className="text-white">Alpha-1</strong>
                    <p className="text-slate-400 text-xs">Pré-analyse parallèle</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-red-400">🔥</span>
                  <div>
                    <strong className="text-white">Alpha-2</strong>
                    <p className="text-slate-400 text-xs">Synthèse raisonnée</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-green-400">📝</span>
                  <div>
                    <strong className="text-white">Alpha-3</strong>
                    <p className="text-slate-400 text-xs">Résumé & extraction</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-purple-400">🧬</span>
                  <div>
                    <strong className="text-white">Alpha-4</strong>
                    <p className="text-slate-400 text-xs">Embeddings mémoire</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Recent Memories */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Database className="w-5 h-5 text-purple-400" />
                Mémoires récentes
              </CardTitle>
            </CardHeader>
            <CardContent>
              {memories.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">Aucune mémoire</p>
              ) : (
                <div className="space-y-2">
                  {memories.slice(0, 5).map((mem) => (
                    <div key={mem.id} className="bg-slate-700/50 rounded p-2 group">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-xs text-slate-300 flex-1">{mem.content?.substring(0, 80)}...</p>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteMemory(mem.id)}
                          className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 p-1 h-auto"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                      <div className="flex gap-2 mt-1">
                        <span className={`text-xs px-1.5 py-0.5 rounded ${
                          mem.type === 'fact' ? 'bg-blue-900/50 text-blue-400' :
                          mem.type === 'preference' ? 'bg-yellow-900/50 text-yellow-400' :
                          'bg-green-900/50 text-green-400'
                        }`}>
                          {mem.type}
                        </span>
                        <span className="text-xs text-slate-500">⭐ {mem.importance}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* History */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-indigo-400" />
                Historique
              </CardTitle>
            </CardHeader>
            <CardContent>
              {history.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">Aucune conversation</p>
              ) : (
                <div className="space-y-2">
                  {history.slice(0, 5).map((conv) => (
                    <div key={conv.id} className="bg-slate-700/50 rounded p-2">
                      <p className="text-xs text-slate-300 truncate">{conv.message?.substring(0, 40)}...</p>
                      <p className="text-xs text-slate-500 mt-1">{new Date(conv.timestamp).toLocaleDateString('fr-FR')}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AlfaFederated;
